// One way to print a document, used by every print button that is not a drug
// label (labels have their own dialog with overflow rules).
//
// The contract is printAgent.js's, restated for whole documents:
//   printer configured + agent up   -> silent, { method: 'agent' }
//   printer configured + agent down -> browser dialog, { warned: true }
//   no printer configured           -> browser dialog, { warned: false }
//
// Callers get `warned` and `popupBlocked` back so they can say what actually
// happened. Nothing here decides what to SHOW — a component that swallowed
// `popupBlocked` would tell staff a document printed when nothing did.
import { printViaAgent } from './printAgent';

// Open a print popup and write a full HTML document into it.
//
// document.write is the right tool here despite its general reputation: the
// target is a blank window this call just opened, the document is a complete
// <html> built by our own generators, and the alternative (building the whole
// print document through the DOM API) buys nothing when the window has no
// other content to clobber. Generators are responsible for escaping the
// clinic data they interpolate — see escapeHtml in receiptHtml.js.
//
// Returns false when nothing was opened, which is the caller's cue that the
// document did NOT print.
export function openPrintWindow(html) {
  try {
    const w = window.open('', '_blank');
    if (!w) return false;
    w.document.write(html);
    w.document.close();
    return true;
  } catch {
    // A hardened browser can hand back a window whose document is unreachable.
    // That is a blocked popup by another name.
    return false;
  }
}

// Add the auto-print script to a document that does not carry one.
//
// The two copies differ by exactly this: the browser needs something to press
// print, the agent must not have it. Injecting before </body> rather than
// appending keeps the script inside the document, which is where a <script>
// after </body> would end up anyway but only by the parser's good grace.
// A document with no </body> gets the script appended, which still runs.
export function withAutoPrint(html) {
  // CLOSE_TAG is built from a variable rather than written as a literal: this
  // file is a bundled module today, but the day any of it is inlined into an
  // HTML <script> block a literal closing tag here would terminate that block
  // early. (Writing it as one string and escaping the slash trips
  // no-useless-escape; concatenating two literals trips no-useless-concat.)
  const CLOSE_TAG = `<${'/'}script>`;
  const script =
    '<script>window.addEventListener("load",function(){setTimeout(function(){'
    + 'window.print();window.onafterprint=function(){window.close();};'
    + '},500);});' + CLOSE_TAG;
  const idx = String(html).lastIndexOf('</body>');
  if (idx === -1) return html + script;
  return html.slice(0, idx) + script + html.slice(idx);
}

// `agentHtml` must NOT auto-print: the agent prints the document itself, and a
// window.print() inside it would fire a second print in the agent's hidden
// window. `browserHtml` is the variant that carries the auto-print script.
// Passing only agentHtml is fine for documents whose template always contains
// the script.
export async function printDocument({
  agentHtml,
  browserHtml,
  printerType,
  branch,
  copies = 1,
}) {
  let popupBlocked = false;

  const result = await printViaAgent(agentHtml, printerType, branch, {
    copies,
    browserFallback: () => {
      popupBlocked = !openPrintWindow(browserHtml || agentHtml);
    },
  });

  return {
    method: result.method,
    warned: !!result.warned,
    popupBlocked,
  };
}
